/**
 * Prospect hooks — Module 4 "Prospecteur IA".
 *
 *  useAudit()     — run a strategic audit (screenshot or text).
 *  usePipeline()  — the CRM board + per-prospect mutations.
 *  useProspectStats() — dashboard KPIs.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

// ── Shared types (mirror src/services/prospect.ts on the API) ────────────────

export type Temperature = 'cold' | 'warm' | 'hot';
export type Stage = 'cold' | 'warm' | 'hot' | 'meeting' | 'client' | 'lost';
export type ProspectCategory =
  | 'jeune_actif'
  | 'parent'
  | 'entrepreneur'
  | 'voyage'
  | 'sportif';

export interface ScoreDimension {
  label: string;
  value: number;
  rationale: string;
}

export interface OpeningMessage {
  angle: string;
  text: string;
}

export interface FollowUpStep {
  timing: string;
  channel: string;
  action: string;
}

export interface PsychProfile {
  summary: string;
  archetype: string;
  drivers: string[];
  values: string[];
  fears: string[];
  communication_style: string;
  decision_style: string;
  awareness_stage: string;
  triggers: string[];
  taboo: string[];
}

export interface AuditAnalysis {
  identity: { display_name: string | null; handle: string | null; platform: string | null };
  headline: string;
  profile_summary: string;
  /** Legacy free-text — kept for backward compat with older audits. */
  psychographic: string;
  /** Structured psychological profile (added 2026-05). May be undefined for old audits. */
  psych_profile?: PsychProfile;
  interests: string[];
  pain_points: string[];
  buying_signals: string[];
  objections: { objection: string; counter: string }[];
  score_breakdown: ScoreDimension[];
  score: number;
  temperature: Temperature;
  category: ProspectCategory;
  approach: string;
  opening_messages: OpeningMessage[];
  follow_up_plan: FollowUpStep[];
}

export interface Prospect {
  id: string;
  handle: string | null;
  display_name: string | null;
  platform: string | null;
  stage: Stage;
  temperature: Temperature | null;
  score: number | null;
  category: ProspectCategory | null;
  notes: string | null;
  next_action: string | null;
  next_action_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Audit response = a Prospect plus its fresh analysis. */
export interface AuditedProspect extends Prospect {
  analysis: AuditAnalysis;
}

/** Detail view = a Prospect plus its latest stored analysis. */
export interface ProspectDetail extends Prospect {
  avatar_url: string | null;
  analysis: AuditAnalysis | null;
  headline: string | null;
}

export interface PipelineBoard {
  board: Record<Stage, Prospect[]>;
  total: number;
}

export interface ProspectStats {
  total: number;
  byStage: Partial<Record<Stage, number>>;
  byTemperature: Partial<Record<Temperature, number>>;
  auditsLast7Days: number;
  followUpsDue: number;
  conversionRate: number;
  inPlay: number;
}

export interface AuditInput {
  text?: string;
  screenshot?: { base64: string; mediaType: string };
  /** A social-profile URL — the API captures it via the headless service. */
  url?: string;
}

/** A reply suggestion produced by Inea for an incoming DM. */
export interface ReplySuggestion {
  angle: string;
  text: string;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

/** Runs a strategic prospect audit. */
export function useAudit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AuditInput) =>
      apiRequest<AuditedProspect>('/prospect/audit', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
      void qc.invalidateQueries({ queryKey: ['prospect-stats'] });
    },
  });
}

/** The CRM pipeline board + stage / field mutations. */
export function usePipeline() {
  const qc = useQueryClient();

  const query = useQuery<PipelineBoard>({
    queryKey: ['pipeline'],
    queryFn: () => apiRequest<PipelineBoard>('/prospect/pipeline'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Prospect> }) =>
      apiRequest<Prospect>(`/prospect/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
      void qc.invalidateQueries({ queryKey: ['prospect-stats'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/prospect/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
      void qc.invalidateQueries({ queryKey: ['prospect-stats'] });
    },
  });

  return { board: query.data, isLoading: query.isLoading, update, remove };
}

/** Loads a single prospect with its latest analysis. */
export function useProspectDetail(id: string | undefined) {
  return useQuery<ProspectDetail>({
    queryKey: ['prospect', id],
    queryFn: () => apiRequest<ProspectDetail>(`/prospect/${id}`),
    enabled: !!id,
  });
}

/** Dashboard KPIs. */
export function useProspectStats() {
  return useQuery<ProspectStats>({
    queryKey: ['prospect-stats'],
    queryFn: () => apiRequest<ProspectStats>('/prospect/stats'),
  });
}

/** Ask Inea for 3 reply suggestions to a DM the user just received. */
export function useReplySuggestions(prospectId: string | undefined) {
  return useMutation({
    mutationFn: (message: string) =>
      apiRequest<{ suggestions: ReplySuggestion[] }>(`/prospect/${prospectId}/replies`, {
        method: 'POST',
        body: { message },
      }),
  });
}

/** One ordered phase of the discovery-call script. */
export interface CallScriptPhase {
  name: string;
  duration: string;
  purpose: string;
  bullets: string[];
}

export interface CallScript {
  objective: string;
  opening: string;
  phases: CallScriptPhase[];
  close: string;
  watch_outs: string[];
}

/** Ask Inea to write a discovery-call script for this prospect. */
export function useCallScript(prospectId: string | undefined) {
  return useMutation({
    mutationFn: () =>
      apiRequest<{ script: CallScript }>(`/prospect/${prospectId}/call-script`, {
        method: 'POST',
      }),
  });
}
